import React from "react";
import { Link } from "react-router-dom";

const effective = "Effective 28 August 2025";

const Section = ({ n, title, children }) => (
  <div style={{padding:'2.5rem 0',borderBottom:'1px solid var(--border)'}}>
    <div style={{display:'flex',alignItems:'baseline',gap:'1.25rem',marginBottom:'1rem'}}>
      <span style={{fontSize:'0.9rem',fontWeight:800,color:'var(--gold)',letterSpacing:'0.06em'}}>{n}</span>
      <h2 style={{fontSize:'1.3rem',fontWeight:800,color:'var(--white)',letterSpacing:'-0.02em'}}>{title}</h2>
    </div>
    <div className="legal-body">{children}</div>
  </div>
);

const PrivacyPolicy = () => {
  return (
    <>
      <div className="page-hero">
        <span className="page-hero-eyebrow">Privacy Policy</span>
        <h1>How we<br />handle your<br /><em>information.</em></h1>
        <p>Peopleology takes privacy seriously. This policy explains what personal information we collect, why we collect it, how we use and protect it, and the rights you have under Australian privacy law.</p>
        <p style={{fontSize:'0.85rem',color:'var(--gold)',marginTop:'0.5rem'}}>{effective}</p>
      </div>

      <section className="section section-black">
        <div style={{maxWidth:'820px'}}>
          <Section n="01" title="Who we are">
            <p>This Privacy Policy is issued by <strong>Peopleology</strong> (ABN 92 397 706 507) (“Peopleology”, “we”, “our”, “us”). We are a real estate business consultancy and recruitment firm based in Victoria, Australia, operating nationally.</p>
            <p>We are bound by the <em>Privacy Act 1988</em> (Cth) and the Australian Privacy Principles (APPs). This policy sets out how we comply with those obligations.</p>
          </Section>

          <Section n="02" title="What personal information we collect">
            <p>The personal information we collect depends on your relationship with us. We only collect information that is reasonably necessary for our functions and activities. This may include:</p>
            <ul className="bullet-list" style={{marginTop:'0.75rem'}}>
              <li>Identity and contact details — name, email, phone, company, role</li>
              <li>Professional information — CV, employment history, qualifications, references, salary expectations, work rights and licence details</li>
              <li>Client information — agency details, brief requirements, hiring history and remuneration data</li>
              <li>Communications — emails, messages, meeting notes and any information you choose to share with us</li>
              <li>Website data — IP address, device, browser type, referring URL, pages viewed and cookies (see section 8)</li>
              <li>Sensitive information — only where you provide it voluntarily and with your consent (e.g. accessibility needs for a workshop)</li>
            </ul>
          </Section>

          <Section n="03" title="How we collect it">
            <p>We collect personal information directly from you — through our website forms, phone calls, emails, meetings, and any documents you send us. In limited circumstances we may collect information from third parties (e.g. reference checks, public professional profiles like LinkedIn, previous employers) but only where it is relevant to a recruitment engagement and, where required, with your consent.</p>
          </Section>

          <Section n="04" title="Why we collect and use it">
            <p>We use personal information for the primary purposes for which it was collected, which include:</p>
            <ul className="bullet-list" style={{marginTop:'0.75rem'}}>
              <li>Providing our consulting, recruitment and speaking services</li>
              <li>Matching candidates to suitable roles with client agencies</li>
              <li>Responding to enquiries and communicating with you</li>
              <li>Managing our client and candidate relationships</li>
              <li>Improving our services and website</li>
              <li>Complying with our legal and regulatory obligations</li>
              <li>Direct marketing (only where you have opted in — you can opt out at any time)</li>
            </ul>
          </Section>

          <Section n="05" title="Who we disclose it to">
            <p>We treat your information as confidential. We may disclose personal information to:</p>
            <ul className="bullet-list" style={{marginTop:'0.75rem'}}>
              <li><strong>Prospective employers</strong> — for candidates, only after we have discussed and agreed the role with you</li>
              <li><strong>Referees</strong> — with your permission, as part of a reference check</li>
              <li><strong>Third-party service providers</strong> — who help us run our business, including cloud hosting (MongoDB Atlas), email delivery (Resend), analytics, and productivity tools. These providers are contractually required to protect your information and only use it for the services they provide to us</li>
              <li><strong>Professional advisers</strong> — lawyers, accountants and insurers, where necessary</li>
              <li><strong>Regulators, courts and law enforcement</strong> — where required or authorised by law</li>
            </ul>
            <p>We do <strong>not</strong> sell your personal information to anyone.</p>
          </Section>

          <Section n="06" title="Overseas disclosure">
            <p>Some of our service providers store or process data outside Australia — typically in the United States, the European Union, or other regions where reputable cloud infrastructure is hosted. Before disclosing information overseas, we take reasonable steps to ensure the recipient is bound by privacy protections substantially similar to the APPs, or your consent to the disclosure.</p>
          </Section>

          <Section n="07" title="How we store and protect it">
            <p>We store personal information in secure, access-controlled cloud systems and, where necessary, in encrypted local files. We use industry-standard security measures including encryption in transit and at rest, access controls, password protection, and monitored infrastructure.</p>
            <p>No system is ever completely secure. If a data breach occurs that is likely to result in serious harm, we will notify affected individuals and the Office of the Australian Information Commissioner (OAIC) in accordance with the Notifiable Data Breaches scheme.</p>
          </Section>

          <Section n="08" title="Cookies and website analytics">
            <p>Our website uses a small number of cookies and similar technologies to operate correctly and understand how visitors use the site. These may include strictly necessary cookies (session, security) and analytics cookies (to measure page visits and traffic sources).</p>
            <p>You can disable cookies through your browser settings. If you do, some parts of the website may not work as intended.</p>
          </Section>

          <Section n="09" title="How long we keep it">
            <p>We only keep personal information for as long as we need it for the purposes we collected it, or as required by law. Typically:</p>
            <ul className="bullet-list" style={{marginTop:'0.75rem'}}>
              <li>Candidate records — kept while you are registered with us and for up to 7 years after our last meaningful contact, unless you ask us to delete earlier</li>
              <li>Client records — kept for the duration of the engagement and for 7 years after (for tax, insurance and accounting purposes)</li>
              <li>Website enquiries — kept for up to 2 years unless a relationship develops</li>
            </ul>
          </Section>

          <Section n="10" title="Your rights">
            <p>Under Australian privacy law you have the right to:</p>
            <ul className="bullet-list" style={{marginTop:'0.75rem'}}>
              <li>Ask what personal information we hold about you</li>
              <li>Ask us to correct information that is inaccurate, out of date or incomplete</li>
              <li>Ask us to delete your information (subject to any legal retention obligations)</li>
              <li>Withdraw consent to marketing at any time</li>
              <li>Complain to us, and — if unresolved — to the OAIC at <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer">oaic.gov.au</a></li>
            </ul>
            <p>To make a request, email <a href="mailto:hello@peopleology.co">hello@peopleology.co</a>. We will respond within 30 days.</p>
          </Section>

          <Section n="11" title="Children">
            <p>Our services are not directed at children. We do not knowingly collect personal information from anyone under 16. If you believe we have collected information from a minor, please contact us and we will delete it.</p>
          </Section>

          <Section n="12" title="Changes to this policy">
            <p>We may update this Privacy Policy from time to time. The current version, and the effective date, will always be published on this page. Material changes will be flagged prominently on our website.</p>
          </Section>

          <Section n="13" title="Contact us">
            <p>Questions, requests or complaints about privacy should be sent to:</p>
            <p style={{marginTop:'0.75rem'}}><strong>Peopleology</strong><br />Privacy Officer<br /><a href="mailto:hello@peopleology.co">hello@peopleology.co</a><br />Geelong · Melbourne · Greater Victoria</p>
          </Section>

          <div style={{padding:'2.5rem 0'}}>
            <p style={{fontSize:'0.8rem',color:'rgba(255,255,255,0.5)',lineHeight:1.7}}>This document is a plain-language summary of our privacy practices and is not legal advice. If you are unsure about any part of this policy, we recommend seeking your own independent legal advice.</p>
          </div>
        </div>
      </section>

      <section className="section section-black2" style={{textAlign:'center',borderTop:'1px solid var(--border)'}}>
        <h2 className="h-md" style={{color:'var(--white)',marginBottom:'1.25rem'}}>Questions about<br />your data?</h2>
        <p style={{fontSize:'0.95rem',color:'rgba(255,255,255,0.70)',maxWidth:'440px',margin:'0 auto 2rem',lineHeight:1.8,fontWeight:300}}>Email Laura directly at hello@peopleology.co and she will personally respond within one business day.</p>
        <Link to="/contact" className="btn btn-gold">Get in touch</Link>
      </section>
    </>
  );
};

export default PrivacyPolicy;
